import { useEffect, useState, useRef } from 'react';
import InlineHexagonLoader from '../HexagonLoader/InlineHexagonLoader';
import { useAuth } from '../../context/AuthContext';
import { useAIService } from '../../context/AIServiceContext';
import InsightMarkdown from './InsightMarkdown';
import AiNarrativeFallback from './AiNarrativeFallback';
import * as Sentry from '@sentry/react';

// Increased retry delay to 120 seconds for better rate limit handling
const RETRY_DELAY_MS = 120000; // 120s (increased from 60s)
const MAX_RETRIES = 3;

const GroupSummary = ({ group, title }) => {
  const { apiFetch } = useAuth();
  const { aiEnabled, isLoading: aiLoading } = useAIService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const retryTimeoutRef = useRef(null);
  const attemptRef = useRef(0);

  // Cleanup timer on unmount
  useEffect(() => () => { if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current); }, []);

  // Fetch only when AI is enabled (from context) and group exists
  useEffect(() => {
    // Wait for AI config to load
    if (aiLoading) return;
    
    // If AI is disabled or no group, don't fetch
    if (!aiEnabled || !group) return;

    let active = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await apiFetch(`/api/insights/it-group-summary?group=${encodeURIComponent(group)}`);
        if (res.status === 204) { if (active) { setText(''); attemptRef.current = 0; } return; }
        if (res.status === 429) throw new Error('RATE_LIMIT');
        if (res.status === 403) throw new Error('FORBIDDEN');
        if (!res.ok) throw new Error('Failed to fetch group insights');
        const json = await res.json().catch(() => null);
        if (active && json) { setText(json.summary || ''); attemptRef.current = 0; }
      } catch (e) {
          Sentry.captureException(e);
        if (!active) return;
        const msg = e.message || 'Error';
        if (msg === 'RATE_LIMIT' && attemptRef.current < MAX_RETRIES) {
          const attempt = ++attemptRef.current;
          setError(`Service busy (429). Retrying in 2 minutes (attempt ${attempt}/${MAX_RETRIES})`);
          retryTimeoutRef.current = setTimeout(() => setReloadToken(t => t + 1), RETRY_DELAY_MS);
        } else if (msg === 'FORBIDDEN') {
          setError('AI Analytics is disabled or API key is invalid. Please check settings.');
        } else {
          setError(msg === 'RATE_LIMIT' ? 'Service busy. Please try again later.' : msg);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [aiEnabled, aiLoading, group, apiFetch, reloadToken]);

  // If AI is loading config, show placeholder
  if (aiLoading) {
    return (
      <div className="dashboard-card insight-card" style={{ marginTop: 4, marginBottom: 0, padding: 12 }}>
        <div className="insight-subtitle" style={{ fontSize: 13, textTransform: 'uppercase', color: '#666', marginBottom: 6 }}>{title || group} Summary</div>
        <div style={{ padding: '16px 0', textAlign: 'center', color: '#666', fontSize: 13 }}>
          <InlineHexagonLoader />
          <div style={{ marginTop: 8 }}>Waiting for AI Insights...</div>
        </div>
      </div>
    );
  }
  
  // If AI is disabled, show a clear deterministic note instead of rendering nothing,
  // so each report section remains self-explanatory for the handover.
  if (!aiEnabled) return <AiNarrativeFallback title={title || group} variant="group" />;

  // Show placeholder while loading
  if (loading && !text && !error) {
    return (
      <div className="dashboard-card insight-card" style={{ marginTop: 4, marginBottom: 0, padding: 12 }}>
        <div className="insight-subtitle" style={{ fontSize: 13, textTransform: 'uppercase', color: '#666', marginBottom: 6 }}>{title || group} Summary</div>
        <div style={{ padding: '16px 0', textAlign: 'center', color: '#666', fontSize: 13 }}>
          <InlineHexagonLoader />
          <div style={{ marginTop: 8 }}>Generating AI insights...</div>
        </div>
      </div>
    );
  }
  
  if (error && !text) return <div className="dashboard-card" style={{ color: error.startsWith('Service busy') ? '#b36b00' : 'red' }}>{title || group} Summary: {error}</div>;
  if (!text) return null;

  return (
    <div className="dashboard-card insight-card" style={{ marginTop: 4, marginBottom: 0, padding: 12 }}>
      <div className="insight-subtitle" style={{ fontSize: 13, textTransform: 'uppercase', color: '#666', marginBottom: 6 }}>{title || group} Summary</div>
      <InsightMarkdown text={text} />
      {error && error.startsWith('Service busy') && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#b36b00' }}>{error}</div>
      )}
    </div>
  );
};

export default GroupSummary;
